import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO/SEO';
import Reveal from '../../components/Reveal/Reveal';
import ArrowIcon from '../../components/icons/ArrowIcon';
import { getServiceSchema } from '../../utils/structuredData';
import {
  IconMobile, IconWeb, IconEnterprise, IconCloud, IconDesign, IconApi,
  IconUsers, IconTarget, IconScale, IconShield,
} from '../../components/icons/ServiceIcons';
import './Services.css';

const PUBLIC_URL = process.env.PUBLIC_URL || '';
const HERO_BG = { '--hero-bg': `url(${PUBLIC_URL}/assets/hero-banner.png)` };
const CTA_BG = { '--cta-bg': `url(${PUBLIC_URL}/assets/cta-banner.png)` };

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const CORE_SERVICES = [
  {
    id: 'custom-software',
    icon: <IconEnterprise size={30} />,
    title: 'Custom Software Development',
    short: 'Business apps shaped around your workflows — not generic templates.',
    description:
      'We design and build software that fits how your team actually works: internal tools, business platforms, portals, and process automation.',
    features: [
      'CRM / ERP-style business applications',
      'Internal portals and admin tools',
      'Workflow and process automation',
      'Role-based access and reporting',
      'Source code handover and documentation',
    ],
    stack: ['.NET', 'React', 'SQL Server', 'Azure'],
  },
  {
    id: 'web-apps',
    icon: <IconWeb size={30} />,
    title: 'Web Application Development',
    short: 'Secure, responsive web apps and customer portals that stay maintainable.',
    description:
      'From customer-facing portals to operations dashboards, we build web applications that are clear to use, secure to run, and practical to extend.',
    features: [
      'Customer and partner portals',
      'Business dashboards and admin panels',
      'Responsive UI for desktop and mobile browsers',
      'REST API backends',
      'Performance and security basics baked in',
    ],
    stack: ['React', 'ASP.NET Core', 'TypeScript', 'PostgreSQL'],
  },
  {
    id: 'mobile-apps',
    icon: <IconMobile size={30} />,
    title: 'Mobile App Development',
    short: 'iOS and Android apps for customers or field teams — MVP to production.',
    description:
      'We help you launch mobile products that support real use cases: customer apps, field workflows, and internal tools, with store submission support when needed.',
    features: [
      'Cross-platform apps (React Native / MAUI)',
      'MVP builds for early validation',
      'Push notifications and offline-friendly flows',
      'App Store and Play Store submission support',
      'Post-launch fixes and updates',
    ],
    stack: ['React Native', 'MAUI Blazor', 'REST APIs'],
  },
  {
    id: 'cloud-devops',
    icon: <IconCloud size={30} />,
    title: 'Cloud & DevOps',
    short: 'Cloud setup, CI/CD, deployments, and monitoring for predictable releases.',
    description:
      'We set up practical cloud and delivery pipelines so environments stay consistent and releases are controlled — without unnecessary complexity.',
    features: [
      'Cloud hosting setup (Azure / AWS)',
      'CI/CD pipelines',
      'Staging and production environments',
      'Monitoring and basic incident readiness',
      'Deployment support for your team',
    ],
    stack: ['Azure', 'AWS', 'Docker', 'GitHub Actions'],
  },
  {
    id: 'ui-ux',
    icon: <IconDesign size={30} />,
    title: 'UI / UX Design',
    short: 'Clear interfaces and prototypes before and during development.',
    description:
      'Good software starts with clarity. We map screens, flows, and interface direction so stakeholders align early — and development has a solid target.',
    features: [
      'User flows and information structure',
      'Wireframes and clickable prototypes',
      'UI design for web and mobile',
      'Design handoff for developers',
      'Usability refinements during build',
    ],
    stack: ['Figma', 'Prototyping', 'Design systems'],
  },
  {
    id: 'api-automation',
    icon: <IconApi size={30} />,
    title: 'API Integration & Automation',
    short: 'Connect systems so data and workflows stop living in silos.',
    description:
      'We integrate payment gateways, CRMs, ERPs, messaging tools, and third-party APIs so your product and operations stay connected.',
    features: [
      'Third-party API integration',
      'Payment gateway connections',
      'CRM / ERP data sync',
      'Webhook and event-based flows',
      'Automation of repetitive business tasks',
    ],
    stack: ['REST APIs', '.NET', 'Node.js'],
  },
];

const SOLUTION_TYPES = [
  {
    title: 'MVP Development',
    text: 'A focused first version to validate your idea with real users — without overbuilding.',
  },
  {
    title: 'Enterprise / Internal Tools',
    text: 'Software for operations, approvals, reporting, and day-to-day team workflows.',
  },
  {
    title: 'E-Commerce & Catalog Apps',
    text: 'Product catalogs, orders, and customer-facing storefront experiences.',
  },
  {
    title: 'Customer Portals',
    text: 'Secure login areas for clients, partners, or members to self-serve.',
  },
  {
    title: 'Progressive Web Apps',
    text: 'Fast, installable web apps that work across devices with a lighter footprint.',
  },
  {
    title: 'Maintenance & Support',
    text: 'Post-launch fixes, improvements, monitoring, and planned feature work.',
  },
];

const BENEFITS = [
  { icon: <IconTarget />, title: 'Clear scope', text: 'We agree deliverables and timelines before heavy development starts.' },
  { icon: <IconUsers />, title: 'Direct communication', text: 'You work with people close to the build — fewer hand-offs.' },
  { icon: <IconShield />, title: 'Secure by default', text: 'Access control and sensible data handling from day one.' },
  { icon: <IconScale />, title: 'Source code ownership', text: 'You receive the code and documentation for what we build.' },
];

const PROCESS = [
  { num: '01', title: 'Discover', text: 'Goals, users, constraints, and a practical scope.' },
  { num: '02', title: 'Design', text: 'Flows, wireframes, and UI direction before coding heavily.' },
  { num: '03', title: 'Build', text: 'Iterative development with demos and feedback loops.' },
  { num: '04', title: 'Launch & Support', text: 'Go-live help, handover, and optional ongoing support.' },
];

const FAQS = [
  {
    q: 'Can you integrate with our existing tools?',
    a: 'Yes. We commonly connect CRMs, ERPs, payment gateways, and other third-party APIs so your systems share data cleanly.',
  },
  {
    q: 'How do you choose the tech stack?',
    a: 'Based on your product needs, team skills, timeline, and long-term maintenance — not trends for their own sake. We often use .NET, React, SQL, and Azure when they fit.',
  },
  {
    q: 'How long does a typical project take?',
    a: 'It depends on scope. A focused MVP can take weeks to a few months; larger business platforms take longer. After discovery we share a realistic timeline.',
  },
  {
    q: 'Do you provide support after launch?',
    a: 'Yes. We can continue with fixes, improvements, monitoring, and planned feature work under a support or dedicated-team arrangement.',
  },
];

const Services = () => {
  const [openFaq, setOpenFaq] = useState(0);
  const siteUrl = process.env.REACT_APP_SITE_URL || 'https://www.codeinventivsolutions.com';

  const structuredData = CORE_SERVICES.map((s) =>
    getServiceSchema(siteUrl, s.title, s.description)
  );

  return (
    <div className="services-page">
      <SEO
        title="Software Development Services | Custom Software, Web & Mobile Apps"
        description="Custom software, web applications, mobile apps, cloud & DevOps, UI/UX, and API integration from CodeInventiv Solutions. Founded in 2025. Based in Jaipur, India."
        keywords="software development services, custom software development, web application development, mobile app development, cloud devops, UI UX design, API integration, software company Jaipur"
        structuredData={structuredData}
        ogType="website"
      />

      <section className="page-hero" style={HERO_BG}>
        <div className="container page-hero-inner">
          <Reveal>
            <div className="section-eyebrow on-dark">Our Services</div>
            <h1 className="page-hero-title">
              Software services that support<br />
              <em>real business needs</em>
            </h1>
            <p className="page-hero-sub">
              From custom business software and web apps to mobile products and cloud setup —
              we help you plan, build, and support practical digital solutions.
            </p>
            <div className="page-hero-points">
              <span><CheckIcon /> Built by an experienced engineering team</span>
              <span><CheckIcon /> Clear scope and communication</span>
              <span><CheckIcon /> Support after launch</span>
            </div>
            <div className="page-hero-actions">
              <Link to="/contact" className="btn btn-gold">
                Get a Quote
                <ArrowIcon />
              </Link>
              <a href="#core-services" className="btn btn-ghost-light">Browse Services</a>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="section section-light" id="core-services">
        <div className="container">
          <Reveal className="section-head">
            <div className="section-eyebrow">What We Offer</div>
            <h2 className="section-title">Core services for product and <em>business software</em></h2>
            <p className="section-sub">
              Choose a service to explore what we deliver — or share your requirement and we will
              recommend the right starting point.
            </p>
          </Reveal>
          <div className="svc-overview-grid">
            {CORE_SERVICES.map((s) => (
              <Reveal key={s.id} as="a" href={`#${s.id}`} className="svc-overview-card">
                <div className="svc-overview-icon">{s.icon}</div>
                <h3>{s.title}</h3>
                <p>{s.short}</p>
                <span className="svc-overview-link">
                  Learn more
                  <ArrowIcon size={14} />
                </span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section section-pale" id="service-details">
        <div className="container">
          <Reveal className="section-head">
            <div className="section-eyebrow">Service Details</div>
            <h2 className="section-title">How we help at each <em>stage of the build</em></h2>
          </Reveal>

          <div className="svc-detail-list">
            {CORE_SERVICES.map((s, index) => (
              <Reveal
                key={s.id}
                as="article"
                id={s.id}
                className={`svc-detail${index % 2 === 1 ? ' reverse' : ''}`}
              >
                <div className="svc-detail-copy">
                  <div className="svc-detail-icon">{s.icon}</div>
                  <h3>{s.title}</h3>
                  <p className="svc-detail-lead">{s.description}</p>
                  <ul className="svc-detail-features">
                    {s.features.map((f) => (
                      <li key={f}><CheckIcon />{f}</li>
                    ))}
                  </ul>
                  <div className="svc-detail-stack">
                    {s.stack.map((tech) => (
                      <span key={tech} className="stack-pill compact">{tech}</span>
                    ))}
                  </div>
                  <Link to="/contact" className="btn btn-gold">
                    Discuss This Service
                    <ArrowIcon />
                  </Link>
                </div>
                <div className="svc-detail-panel">
                  <div className="svc-detail-panel-label">Included focus</div>
                  <ol>
                    {s.features.slice(0, 4).map((f, i) => (
                      <li key={f}>
                        <span>{String(i + 1).padStart(2, '0')}</span>
                        {f}
                      </li>
                    ))}
                  </ol>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section-dark solutions-band" id="solutions">
        <div className="container">
          <Reveal className="section-head">
            <div className="section-eyebrow on-dark">Solutions We Build</div>
            <h2 className="section-title on-dark">Tailored software for common <em>business needs</em></h2>
            <p className="dark-lede" style={{ marginTop: '1rem' }}>
              Beyond service categories, these are the kinds of products and systems clients usually ask for.
            </p>
          </Reveal>
          <div className="solutions-grid">
            {SOLUTION_TYPES.map((item) => (
              <Reveal key={item.title} as="article" className="solution-card">
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section section-light" id="why-us">
        <div className="container">
          <Reveal className="section-head">
            <div className="section-eyebrow">Why Work With Us</div>
            <h2 className="section-title">Delivery habits that keep projects <em>easy to follow</em></h2>
          </Reveal>
          <div className="benefits-grid">
            {BENEFITS.map((b) => (
              <Reveal key={b.title} as="article" className="benefit-card">
                <div className="benefit-icon">{b.icon}</div>
                <h3>{b.title}</h3>
                <p>{b.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section section-pale" id="process">
        <div className="container">
          <Reveal className="section-head">
            <div className="section-eyebrow">How We Work</div>
            <h2 className="section-title">A simple process from <em>idea to launch</em></h2>
          </Reveal>
          <ol className="process-grid">
            {PROCESS.map((step) => (
              <Reveal key={step.num} as="li" className="process-step">
                <div className="process-num">{step.num}</div>
                <h3 className="process-title">{step.title}</h3>
                <p className="process-text">{step.text}</p>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      <section className="section section-light" id="faq">
        <div className="container faq-wrap">
          <Reveal className="section-head">
            <div className="section-eyebrow">FAQ</div>
            <h2 className="section-title">Common questions about <em>our services</em></h2>
          </Reveal>
          <div className="faq-list">
            {FAQS.map((item, i) => {
              const open = openFaq === i;
              return (
                <Reveal key={item.q} className={`faq-item${open ? ' open' : ''}`}>
                  <button
                    type="button"
                    className="faq-question"
                    aria-expanded={open}
                    onClick={() => setOpenFaq(open ? -1 : i)}
                  >
                    {item.q}
                    <span aria-hidden="true">{open ? '−' : '+'}</span>
                  </button>
                  {open && <p className="faq-answer">{item.a}</p>}
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="cta-band" style={CTA_BG}>
        <div className="container cta-inner">
          <Reveal className="cta-copy">
            <h2>Tell us what you need. <em>We&apos;ll suggest a clear next step.</em></h2>
            <p>
              Share a short brief and we will come back with questions, a suggested approach, and
              next steps — at no cost.
            </p>
          </Reveal>
          <Reveal className="cta-actions">
            <Link to="/contact" className="btn btn-gold">
              Request a Quote
              <ArrowIcon />
            </Link>
            <Link to="/" className="btn btn-ghost-light">Back to Home</Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Services;
